import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const EntityTable = ({ entities, entityType, language, onEdit, onDelete, onView }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const labels = {
    name: { en: 'Name', ar: 'الاسم' },
    location: { en: 'Location', ar: 'الموقع' },
    serviceTypes: { en: 'Service Types', ar: 'أنواع الخدمات' },
    actions: { en: 'Actions', ar: 'الإجراءات' },
    rowsPerPage: { en: 'Rows per page:', ar: 'الصفوف لكل صفحة:' },
    previous: { en: 'Previous', ar: 'السابق' },
    next: { en: 'Next', ar: 'التالي' },
    of: { en: 'of', ar: 'من' }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedEntities = [...entities]?.sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];

    if (sortField === 'location') {
      aVal = a?.location?.address || '';
      bVal = b?.location?.address || '';
    }

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = sortedEntities?.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(sortedEntities?.length / recordsPerPage);

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto scrollbar-custom">
        <table className="w-full min-w-[700px]">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-4 py-3 md:px-6 md:py-4 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                >
                  {labels?.name?.[language]}
                  <Icon name={sortField === 'name' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
                </button>
              </th>
              <th className="px-4 py-3 md:px-6 md:py-4 text-left">
                <button
                  onClick={() => handleSort('location')}
                  className="flex items-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                >
                  {labels?.location?.[language]}
                  <Icon name={sortField === 'location' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
                </button>
              </th>
              <th className="px-4 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-semibold text-foreground">
                {labels?.serviceTypes?.[language]}
              </th>
              <th className="px-4 py-3 md:px-6 md:py-4 text-center text-xs md:text-sm font-semibold text-foreground">
                {labels?.actions?.[language]}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {currentRecords?.map((entity) => (
              <tr key={entity?.id} className="hover:bg-muted/50 transition-smooth">
                <td className="px-4 py-3 md:px-6 md:py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                      {entity?.images?.[0] ? (
                        <Image
                          src={entity?.images?.[0]}
                          alt={`${entity?.name} ${entityType} image`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon name="Image" size={20} className="text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <p className="text-sm md:text-base font-medium text-foreground">{entity?.name}</p>
                  </div>
                </td>
                <td className="px-4 py-3 md:px-6 md:py-4">
                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={14} className="text-text-secondary" />
                    <p className="text-xs md:text-sm text-text-secondary">{entity?.location?.address}</p>
                  </div>
                </td>
                <td className="px-4 py-3 md:px-6 md:py-4">
                  <div className="flex flex-wrap gap-1">
                    {entity?.serviceTypes?.slice(0, 2)?.map((service, index) => (
                      <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {service}
                      </span>
                    ))}
                    {entity?.serviceTypes?.length > 2 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-text-secondary">
                        +{entity?.serviceTypes?.length - 2}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 md:px-6 md:py-4">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => onView(entity)}
                      className="p-1.5 md:p-2 rounded-lg hover:bg-primary/10 text-primary transition-smooth"
                      aria-label={`View ${entityType}`}
                    >
                      <Icon name="Eye" size={16} />
                    </button>
                    <button
                      onClick={() => onEdit(entity)}
                      className="p-1.5 md:p-2 rounded-lg hover:bg-warning/10 text-warning transition-smooth"
                      aria-label={`Edit ${entityType}`}
                    >
                      <Icon name="Edit2" size={16} />
                    </button>
                    <button
                      onClick={() => onDelete(entity)}
                      className="p-1.5 md:p-2 rounded-lg hover:bg-error/10 text-error transition-smooth"
                      aria-label={`Delete ${entityType}`}
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 md:px-6 md:py-4 border-t border-border bg-muted/30">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs md:text-sm text-text-secondary caption">{labels?.rowsPerPage?.[language]}</span>
            <select
              value={recordsPerPage}
              onChange={(e) => {
                setRecordsPerPage(Number(e?.target?.value));
                setCurrentPage(1);
              }}
              className="px-2 py-1 md:px-3 md:py-1.5 rounded-lg border border-border bg-background text-xs md:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs md:text-sm text-text-secondary caption">
              {indexOfFirstRecord + 1}-{Math.min(indexOfLastRecord, entities?.length)} {labels?.of?.[language]} {entities?.length}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                iconName="ChevronLeft"
                iconSize={16}
              >
                <span className="hidden sm:inline">{labels?.previous?.[language]}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                iconName="ChevronRight"
                iconPosition="right"
                iconSize={16}
              >
                <span className="hidden sm:inline">{labels?.next?.[language]}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntityTable;